import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, uploadAudio } from "../api/client";
import { toast } from "../components/toast";

// Replacing a file set, once the reviewer has made their choices. The uploads
// and the filing run HERE, above the router, so the dialog can be hidden and the
// work goes on while you use the rest of Mockingbird in this tab: a small pill
// and History show how far it has got, and a message says when it is done.
//
// The work lives in this tab - the files can only be read here - so closing the
// tab pauses it. Each file is brought in AND filed before the next is touched,
// so a pause leaves every file either replaced or as it was, never a new copy
// sitting beside the file it was meant to replace. The choices are remembered
// (localStorage, without the files), and dropping the same files again carries
// on where it stopped. Filing is safe to repeat: the server stacks a file once
// and carries a proof once, so a request cut off by the close can just be sent
// again.

export interface ReplaceItem {
  name: string;                 // the delivery's own file name
  size: number;                 // to know the same file when it is dropped again
  current: string | null;       // the file it replaces; null = a new file
  seconds: number | null;
  apply_work: boolean;          // keep the proofing and pickups
  // keep: proofing and pickups as they were · reproof: the pickups, and the new
  // audio proofed again beside them · drop: neither. Absent on a job saved by
  // an earlier version of the page: apply_work decides.
  work?: "keep" | "reproof" | "drop";
  stored?: string;              // the storage name its upload was given
  filed?: boolean;              // filed in place
}

/** running: going now · stopped: a request failed, Try again carries on ·
 *  paused: the tab closed (or files are still to be dropped again). */
export type ReplaceStatus = "running" | "stopped" | "paused";

export interface ReplaceJob {
  projectId: string;
  projectName: string;
  items: ReplaceItem[];
  status: ReplaceStatus;
  step: "uploading" | "filing" | null;
  now: string | null;           // the file being worked on
  pct: number;                  // its upload, 0–100
  error: string | null;
  carried: number;
  stale: number;
  reproofing: number;           // being proofed again, their pickups kept
}

interface Saved {
  projectId: string; projectName: string; items: ReplaceItem[];
  carried: number; stale: number; reproofing?: number;
  tab: string; beat: number;
}

interface ReplaceJobsApi {
  jobs: ReplaceJob[];
  get: (projectId: string) => ReplaceJob | undefined;
  /** Hand over the reviewed choices and their files, and start. */
  start: (projectId: string, projectName: string, items: ReplaceItem[], files: File[]) => void;
  /** Carry on a stopped or paused job, with any files dropped again. Returns
   *  the names it still has no file for. */
  resume: (projectId: string, files?: File[]) => string[];
  /** Stop for good. What is in stays in. */
  discard: (projectId: string) => void;
  /** The files a paused job still needs dropped again. */
  missing: (projectId: string) => string[];
  /** The job whose dialog is open (else it shows as a pill). */
  shown: string | null;
  show: (projectId: string | null) => void;
  runningCount: number;
}

const KEY = "mb.replaceJobs";
const BEAT_MS = 5000;
// Another tab's job counts as running there while it has beaten this recently.
const ALIVE_MS = 20000;

// This tab, across reloads of it (which is exactly when a job becomes paused).
const TAB = (() => {
  try {
    const t = sessionStorage.getItem("mb.tab") || Math.random().toString(36).slice(2);
    sessionStorage.setItem("mb.tab", t);
    return t;
  } catch { return Math.random().toString(36).slice(2); }
})();

function load(): Saved[] {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}
function save(all: Saved[]) {
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* private window: no pause */ }
}

export const doneCount = (j: ReplaceJob) => j.items.filter((i) => i.filed).length;
/** How far it has got: files filed, and the one being uploaded for as far as
 *  it has got (filing is quick). */
export function progressOf(j: ReplaceJob) {
  const total = j.items.length;
  const done = doneCount(j);
  const part = j.step === "uploading" ? j.pct / 100 : j.step === "filing" ? 1 : 0;
  return { total, done, pct: total ? Math.min(100, ((done + part) / total) * 100) : 0 };
}
/** Files still waiting to be dropped again before a paused job can finish. */
export const needsFiles = (j: ReplaceJob, files?: Map<string, File>) =>
  j.items.filter((i) => !i.filed && !i.stored && !files?.has(i.name)).map((i) => i.name);

const Ctx = createContext<ReplaceJobsApi | null>(null);

export function ReplaceJobsProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const store = useRef<Map<string, ReplaceJob>>(new Map());
  const files = useRef<Map<string, Map<string, File>>>(new Map());
  const [tick, bump] = useState(0);
  const sync = useCallback(() => bump((n) => n + 1), []);
  const [shown, setShown] = useState<string | null>(null);

  // Write this tab's jobs, leaving any another tab is running as they are.
  const persist = useCallback(() => {
    const mine = [...store.current.values()];
    const ids = new Set(mine.map((j) => j.projectId));
    const others = load().filter((s) => !ids.has(s.projectId));
    save([...others, ...mine.map((j) => ({
      projectId: j.projectId, projectName: j.projectName, items: j.items,
      carried: j.carried, stale: j.stale, reproofing: j.reproofing, tab: TAB, beat: Date.now(),
    }))]);
  }, []);
  const forget = useCallback((projectId: string) => {
    store.current.delete(projectId);
    files.current.delete(projectId);
    save(load().filter((s) => s.projectId !== projectId));
  }, []);

  // A job remembered from a tab that closed (or this one, reloaded) is paused.
  // One another tab is still running is left alone.
  useEffect(() => {
    for (const s of load()) {
      if (store.current.has(s.projectId)) continue;
      if (s.tab !== TAB && Date.now() - s.beat < ALIVE_MS) continue;
      if (!s.items?.some((i) => !i.filed)) { save(load().filter((x) => x.projectId !== s.projectId)); continue; }
      store.current.set(s.projectId, {
        projectId: s.projectId, projectName: s.projectName, items: s.items,
        status: "paused", step: null, now: null, pct: 0, error: null,
        carried: s.carried || 0, stale: s.stale || 0, reproofing: s.reproofing || 0,
      });
    }
    sync();
  }, [sync]);

  const finish = useCallback((job: ReplaceJob) => {
    const n = job.items.length;
    forget(job.projectId);
    setShown((s) => (s === job.projectId ? null : s));
    qc.invalidateQueries({ queryKey: ["projects"] });
    toast(
      `${job.projectName ? `${job.projectName}: ` : ""}${n} file${n === 1 ? "" : "s"} brought in`
      + (job.carried ? `, ${job.carried} kept their proofing` : "")
      + (job.stale ? ` (${job.stale} flagged for re-proof)` : "")
      + (job.reproofing ? `, ${job.reproofing} being proofed again with their pickups kept` : ""),
      "success",
    );
    sync();
  }, [forget, qc, sync]);

  const run = useCallback(async (projectId: string) => {
    const job = store.current.get(projectId);
    if (!job || job.status === "running") return;
    job.status = "running"; job.error = null;
    persist(); sync();
    try {
      for (const it of job.items) {
        if (it.filed) continue;
        if (!it.stored) {
          const file = files.current.get(projectId)?.get(it.name);
          if (!file) continue;   // to be dropped again
          job.step = "uploading"; job.now = it.name; job.pct = 0; sync();
          // The upload picks the storage name - a re-delivery of a name already
          // here is written to one of its own - so filing must be told the name
          // it actually got, not the one it was offered.
          it.stored = await uploadAudio(projectId, file, (p) => { job.pct = p; sync(); }, undefined, false);
          persist();
        }
        job.step = "filing"; job.now = it.name; sync();
        // One request per file: each copies a chapter's proofing, and a whole
        // delivery in one request could outlast the connection.
        const one = await api<{ carried: { reproof_needed?: boolean }[]; reproofing?: string[] }>(
          `/api/projects/${projectId}/replace/apply`,
          { method: "POST", body: JSON.stringify({ pairs: [{
            incoming: it.stored, current: it.current, seconds: it.seconds, apply_work: it.apply_work,
            ...(it.work ? { work: it.work } : {}),
          }] }) },
        );
        it.filed = true;
        const again = one.reproofing?.length ?? 0;
        job.reproofing += again;
        job.carried += Math.max(0, one.carried.length - again);
        job.stale += one.carried.filter((c) => c.reproof_needed).length;
        persist();
        // The file list is its OWN query - the project alone left it showing the
        // delivery's predecessors until something else refetched it.
        qc.invalidateQueries({ queryKey: ["audio", projectId] });
        qc.invalidateQueries({ queryKey: ["project", projectId] });
        sync();
      }
      job.step = null; job.now = null;
      if (job.items.every((i) => i.filed)) { finish(job); return; }
      job.status = "paused";
      persist(); sync();
    } catch (e: unknown) {
      job.status = "stopped"; job.step = null;
      job.error = (e as Error)?.message || "Could not bring the files in.";
      qc.invalidateQueries({ queryKey: ["audio", projectId] });
      persist(); sync();
    }
  }, [finish, persist, qc, sync]);

  const start = useCallback((projectId: string, projectName: string, items: ReplaceItem[], fs: File[]) => {
    if (store.current.get(projectId)?.status === "running") return;
    files.current.set(projectId, new Map(fs.map((f) => [f.name, f])));
    store.current.set(projectId, {
      projectId, projectName, items: items.map((i) => ({ ...i })),
      status: "paused", step: null, now: null, pct: 0, error: null, carried: 0, stale: 0, reproofing: 0,
    });
    void run(projectId);
  }, [run]);

  const resume = useCallback((projectId: string, fs?: File[]) => {
    const job = store.current.get(projectId);
    if (!job) return [];
    if (fs?.length) {
      // The same file: same name and size. A different take that happens to
      // share a name must not be filed under the choices made for another.
      const want = new Map(job.items.map((i) => [i.name, i.size]));
      const have = files.current.get(projectId) ?? new Map<string, File>();
      for (const f of fs) if (want.get(f.name) === f.size) have.set(f.name, f);
      files.current.set(projectId, have);
    }
    const missing = needsFiles(job, files.current.get(projectId));
    const ready = job.items.some((i) => !i.filed && (i.stored || files.current.get(projectId)?.has(i.name)));
    if (ready) void run(projectId); else sync();
    return missing;
  }, [run, sync]);

  const discard = useCallback((projectId: string) => {
    const job = store.current.get(projectId);
    if (!job || job.status === "running") return;
    const n = doneCount(job);
    forget(projectId);
    setShown((s) => (s === projectId ? null : s));
    qc.invalidateQueries({ queryKey: ["audio", projectId] });
    toast(`${n} of ${job.items.length} file${job.items.length === 1 ? "" : "s"} brought in; the rest were left as they were.`, "info");
    sync();
  }, [forget, qc, sync]);

  const jobs = useMemo(() => [...store.current.values()].map((j) => ({ ...j })), [tick]);
  const runningCount = jobs.filter((j) => j.status === "running").length;

  // While anything runs: a heartbeat, so another tab can tell it is still
  // going, and a warning before the tab closes (which would pause it).
  useEffect(() => {
    if (!runningCount) return;
    const beat = window.setInterval(persist, BEAT_MS);
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => { window.clearInterval(beat); window.removeEventListener("beforeunload", onBeforeUnload); };
  }, [runningCount, persist]);

  const value: ReplaceJobsApi = {
    jobs,
    get: (id) => jobs.find((j) => j.projectId === id),
    start, resume, discard,
    missing: (id) => { const j = store.current.get(id); return j ? needsFiles(j, files.current.get(id)) : []; },
    shown, show: setShown,
    runningCount,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useReplaceJobs(): ReplaceJobsApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useReplaceJobs must be used within <ReplaceJobsProvider>");
  return ctx;
}
